import React from "react";
import { motion } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const About = () => {
  // Enhanced carousel settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: true,
    cssEase: "ease-in-out",
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
  };

  // Project data (unchanged)
  const projects = [
    {
      title: "Restaurant App",
      description:
        "A full-featured restaurant application that allows users to browse menus, place orders, and manage reservations. Designed with a modern UI and powered by RESTful APIs for smooth and dynamic data handling.",
      image: "./images/restaurant.gif",
      demoLink: "https://restaurant-management-system-xi-one.vercel.app",
      githubLink: "https://github.com/susank00/Restaurant-management-system",
    },
    {
      title: "School Management System",
      description:
        "A comprehensive web application designed to manage various academic and administrative tasks in a school. Features include student enrollment, teacher management, attendance tracking, class scheduling, and user authentication for students, teachers, and admins.",
      image: "./images/school.gif",
      demoLink: "https://nexvidya.nexgeninnovs.com/",
    },
    {
      title: "PetStore",
      description:
        "The Dynamic PetStore app is built with the MERN stack, featuring login, buy, and payment functionalities.",
      image: "./images/pet.png",
      demoLink: "https://pet-store-eight.vercel.app",
      githubLink: "https://github.com/susank00/pet-store",
    },
  ];

  // Card animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
    hover: { scale: 1.05, boxShadow: "0px 10px 30px rgba(0, 255, 255, 0.2)" },
  };

  return (
    <div className=" min-h-screen p-8 overflow-hidden">
      {/* Background decorative elements */}

      <motion.h1
        style={{ fontFamily: "'Playfair Display', cursive" }}
        className="text-teal-300 text-5xl md:text-6xl text-center p-1 font-extrabold tracking-tight  z-10"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        My Projects
        <div className="w-24 h-1 bg-teal-400  rounded-full"></div>
      </motion.h1>

      <div className="flex justify-center ">
        <div className="w-full  rounded-xl max-w-8xl">
          <Slider {...settings} className="w-full">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                className="px-3 shadow-2xl  "
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                }}
                whileHover={{ rotateY: 15, rotateX: -15 }}
              >
                <div className="bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-700 shadow-2xl flex flex-col max-w-sm mx-auto h-[460px] overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-indigo-500/50">
                  <div className=" w-full h-56 overflow-hidden">
                    <img
                      className="rounded-t-2xl object-cover w-full h-full transition-transform duration-500 hover:scale-110"
                      src={project.image}
                      alt={project.title}
                    />
                    <div className=" rounded-3xl inset-0 shadow-2xl bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                  </div>

                  <div className="flex flex-col flex-grow p-3">
                    <h5
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      className="text-teal-300 text-2xl font-bold tracking-tight "
                    >
                      {project.title}
                    </h5>
                    <p
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      className="text-gray-200 text-sm leading-relaxed flex-grow"
                    >
                      {project.description}
                    </p>

                    <div className="flex gap-3 mt-5">
                      <a
                        href={project.demoLink || "#"}
                        className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-300 ${
                          project.demoLink
                            ? "bg-teal-600 hover:bg-teal-700 focus:ring-4 focus:ring-teal-300"
                            : "bg-gray-600 cursor-not-allowed"
                        }`}
                      >
                        Demo
                        <svg
                          className="w-4 h-4 ml-2"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 14 10"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M1 5h12m0 0L9 1m4 4L9 9"
                          />
                        </svg>
                      </a>
                      <a
                        href={project.githubLink}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-600 focus:ring-4 focus:ring-gray-300 transition-all duration-300"
                      >
                        Github
                        <svg
                          className="w-4 h-4 ml-2"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 14 10"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M1 5h12m0 0L9 1m4 4L9 9"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default About;
